'use client';

import { motion } from 'framer-motion';
import { isValidPhoneNumber,parsePhoneNumber } from 'libphonenumber-js';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import Script from 'next/script';
import React, { useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';

import Button from '@/components/Button';
import DoctorIntro from '@/components/DoctorIntro';
// import FloatingChat from '@/components/FloatingChat'; // PASIF HALE GETIRILDI
import Header from '@/components/Header';
import HeroSlider from '@/components/HeroSlider';
// import PatientTestimonials from '@/components/PatientTestimonials';
// import TestimonialVideos from '@/components/TestimonialVideos';

import { useLanguage } from '@/contexts/LanguageContext';

export default function HomePage() {
  const router = useRouter();
  const pathname = usePathname();
  const { t, localeUrls, locale } = useLanguage();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Form validation
  const validateForm = () => {
    // İsim kontrolü
    if (!formData.name.trim()) {
      toast.error('Lütfen adınızı girin.');
      return false;
    }

    if (formData.name.trim().length < 2) {
      toast.error('Ad en az 2 karakter olmalıdır.');
      return false;
    }

    // Telefon kontrolü
    if (!formData.phone.trim()) {
      toast.error('Lütfen telefon numaranızı girin.');
      return false;
    }

    // Telefon numarası validasyonu
    try {
      const phoneNumber = parsePhoneNumber(formData.phone);
      if (!phoneNumber || !isValidPhoneNumber(formData.phone)) {
        toast.error('Lütfen geçerli bir telefon numarası girin. (Örn: +90 532 123 45 67)');
        return false;
      }
    } catch (error) {
      toast.error('Lütfen geçerli bir telefon numarası girin. (Örn: +90 532 123 45 67)');
      return false;
    }

    // Mesaj kontrolü
    if (!formData.message.trim()) {
      toast.error('Lütfen mesajınızı yazın.');
      return false;
    }

    if (formData.message.trim().length < 10) {
      toast.error('Mesaj en az 10 karakter olmalıdır.');
      return false;
    }

    return true;
  };

  // Form submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form validasyonu
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          locale: locale
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', message: '' }); // Form sıfırla
        toast.success('Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.');
      } else {
        setSubmitStatus('error');
        const errorData = await response.json();
        toast.error(errorData.message || 'Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Form gönderim hatası:', error);
      setSubmitStatus('error');
      toast.error('Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Telefon input'u için özel işlem
    if (name === 'phone') {
      // Sadece rakam, +, boşluk ve () karakterlerine izin ver
      const phoneValue = value.replace(/[^0-9+\s()-]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: phoneValue
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      {/* SEO H1 - Gizli ama arama motorları için mevcut */}
      <h1 className="sr-only">Op. Dr. Barış Çın - İstanbul Estetik Cerrahi Uzmanı 2026</h1>
      <Script
        id="local-business-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            "@id": "https://www.drbariscin.com/#medicalbusiness",
            "name": "Op. Dr. Barış Çın",
            "alternateName": "Dr. Barış Çın Estetik Cerrahi",
                          "image": [
                "https://www.drbariscin.com/images/doktor.webp",
                "https://www.drbariscin.com/images/klinik.webp",
                "https://www.drbariscin.com/images/ameliyathane.webp"
              ],
            "description": "İstanbul'da estetik cerrahi uzmanı Op. Dr. Barış Çın ile 2026'te güvenli ve doğal sonuçlar! 15+ yıl deneyim, burun estetiği, yüz gençleştirme, vücut şekillendirme.",
            "medicalSpecialty": [
              "PlasticSurgery",
              "CosmeticSurgery",
              "Rhinoplasty",
              "FacialPlasticSurgery"
            ],
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Koşuyolu, Mehmet Akfan Sokağı No:19",
              "addressLocality": "Kadıköy",
              "addressRegion": "İstanbul",
              "postalCode": "34718",
              "addressCountry": "TR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "40.9860",
              "longitude": "29.0485"
            },
            "url": "https://www.drbariscin.com",
            "telephone": "+905438999586",
            "email": "info@bariscin.com",
            "priceRange": "₺₺₺",
            "currenciesAccepted": "TRY, EUR, USD",
            "paymentAccepted": "Cash, Credit Card, Bank Transfer",
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "09:00",
                "closes": "18:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday",
                "opens": "09:00",
                "closes": "16:00"
              }
            ],
            "sameAs": [
              "https://www.instagram.com/drbariscin",
              "https://www.facebook.com/drbariscin",
              "https://twitter.com/bariscin",
              "https://www.linkedin.com/in/bariscin"
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Estetik Cerrahi Hizmetleri 2026",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "MedicalProcedure",
                    "name": "Burun Estetiği (Rinoplasti)",
                    "description": "Doğal ve güvenli burun estetiği operasyonu. 2026 güncel teknikler.",
                    "medicalSpecialty": "PlasticSurgery"
                  },
                  "priceRange": "₺15000-₺35000",
                  "availability": "InStock"
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "MedicalProcedure",
                    "name": "Yüz Estetiği ve Gençleştirme",
                    "description": "Yüz germe, göz kapağı estetiği ve yüz gençleştirme işlemleri.",
                    "medicalSpecialty": "FacialPlasticSurgery"
                  },
                  "priceRange": "₺20000-₺50000",
                  "availability": "InStock"
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "MedicalProcedure",
                    "name": "Vücut Estetiği",
                    "description": "Liposuction, karın germe, meme estetiği ve vücut şekillendirme.",
                    "medicalSpecialty": "PlasticSurgery"
                  },
                  "priceRange": "₺25000-₺80000",
                  "availability": "InStock"
                }
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "150",
              "bestRating": "5",
              "worstRating": "1"
            },
            "founder": {
              "@type": "Person",
              "name": "Op. Dr. Barış Çın",
              "jobTitle": "Estetik ve Plastik Cerrahi Uzmanı",
              "alumniOf": "İstanbul Üniversitesi Tıp Fakültesi",
              "memberOf": "Türk Plastik Rekonstrüktif ve Estetik Cerrahi Derneği"
            }
          })
        }}
      />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 5000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Header />
      <HeroSlider />
      <div className="mt-8 sm:mt-10">
        <DoctorIntro />
      </div>

      {/* Services Section */}
      <section 
        className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10"
        aria-labelledby="services-title"
      >
        <div className="text-center mb-12">
          <motion.h2
            id="services-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl sm:text-4xl font-bold text-center mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"
          >
            {t.homepage.services.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-lg text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 max-w-2xl mx-auto"
          >
            {t.homepage.services.subtitle}
          </motion.p>
        </div>
        <div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          role="list"
          aria-label="Hizmetler"
        >
          {/* Service Card 1 */}
          <div 
            className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            role="listitem"
          >
            <div className="relative h-48">
              <Image
                src="/images/burun.webp"
                alt={t.homepage.services.cards.rhinoplasty.title}
                fill
                loading="lazy"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                className="object-cover"
              />
            </div>
            <div className="p-3">
              <h3 className="text-xl font-bold text-[#181C32] mb-1">{t.homepage.services.cards.rhinoplasty.title}</h3>
              <p className="text-[#181C32]/80 mb-2">
                {t.homepage.services.cards.rhinoplasty.description}
              </p>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => router.push(localeUrls.burunEstetigi)}
              >
                {t.homepage.services.cards.rhinoplasty.button}
              </Button>
            </div>
          </div>

          {/* Service Card 2 */}
          <div 
            className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            role="listitem"
          >
            <div className="relative h-48">
              <Image
                src="/images/yuz-germe.webp"
                alt={t.homepage.services.cards.faceLift.title}
                fill
                loading="lazy"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                className="object-cover"
              />
            </div>
            <div className="p-3">
              <h3 className="text-xl font-bold text-[#181C32] mb-1">{t.homepage.services.cards.faceLift.title}</h3>
              <p className="text-[#181C32]/80 mb-2">
                {t.homepage.services.cards.faceLift.description}
              </p>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => router.push(localeUrls.yuzGerme)}
              >
                {t.homepage.services.cards.faceLift.button}
              </Button>
            </div>
          </div>

          {/* Service Card 3 */}
          <div 
            className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            role="listitem"
          >
            <div className="relative h-48">
              <Image
                src="/images/karin-germe.webp"
                alt={t.homepage.services.cards.bodyAesthetics.title}
                fill
                loading="lazy"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                className="object-cover"
              />
            </div>
            <div className="p-3">
              <h3 className="text-xl font-bold text-[#181C32] mb-1">{t.homepage.services.cards.bodyAesthetics.title}</h3>
              <p className="text-[#181C32]/80 mb-2">
                {t.homepage.services.cards.bodyAesthetics.description}
              </p>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => router.push(localeUrls.karinGerme)}
              >
                {t.homepage.services.cards.bodyAesthetics.button}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Videos Section - hidden for all locales */}
      {/* {locale !== 'tr' && (
        <>
          <TestimonialVideos />
          <div className="mt-6 sm:mt-10">
            <PatientTestimonials />
          </div>
        </>
      )} */}

      {/* Contact Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-5">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 sm:p-12 text-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                {t.homepage.contact.title}
              </h2>
              <p className="text-lg mb-6">
                {t.homepage.contact.subtitle}
              </p>
              <div className="space-y-4">
                <a 
                  href={`https://wa.me/905438999586?text=${encodeURIComponent(t.whatsapp.appointmentMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:text-blue-200 transition-colors cursor-pointer"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>+90 543 899 95 86</span>
                </a>
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>drbariscin86@gmail.com</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t.homepage.contact.name}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 disabled:opacity-50"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Telefon (+90 532 123 45 67)"
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 disabled:opacity-50"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={t.homepage.contact.message}
                    rows={4}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 disabled:opacity-50"
                  ></textarea>
                </div>
                <Button
                  type='submit'
                  variant='light'
                  disabled={isSubmitting}
                  className='w-full font-semibold py-3 rounded-lg transition text-base disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isSubmitting ? 'Gönderiliyor...' : t.homepage.contact.send}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials / Yetki Belgeleri Section */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
        aria-labelledby="credentials-title"
      >
        <div className="text-center mb-10">
          <motion.h2
            id="credentials-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl sm:text-4xl font-bold text-center mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"
          >
            {t.homepage.credentials.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-base sm:text-lg text-[#181C32]/70 max-w-2xl mx-auto"
          >
            {t.homepage.credentials.subtitle}
          </motion.p>
        </div>

        <div className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 md:grid md:grid-cols-2 md:gap-8 md:overflow-visible scrollbar-hide">
          {/* Health Türkiye Card */}
          <motion.a
            href="/images/health-turkiye.jpg"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="group relative min-w-[85%] md:min-w-0 snap-center bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:-translate-y-1"
          >
            <div className="relative aspect-[4/3] bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-8">
              <Image
                src="/images/health-turkiye.jpg"
                alt={t.homepage.credentials.healthTurkiyeTitle}
                fill
                sizes="(max-width: 768px) 85vw, 50vw"
                className="object-contain p-6 group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-[#181C32] mb-2">
                {t.homepage.credentials.healthTurkiyeTitle}
              </h3>
              <p className="text-sm text-[#181C32]/70 mb-4">
                {t.homepage.credentials.healthTurkiyeDescription}
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 group-hover:text-purple-600 transition-colors">
                {t.homepage.credentials.viewImage}
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </div>
          </motion.a>

          {/* Yetki Belgesi Card */}
          <motion.a
            href="/images/yetki-belgesi.jpg"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="group relative min-w-[85%] md:min-w-0 snap-center bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:-translate-y-1"
          >
            <div className="relative aspect-[4/3] bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
              <Image
                src="/images/yetki-belgesi.jpg"
                alt={t.homepage.credentials.certificateTitle}
                fill
                sizes="(max-width: 768px) 85vw, 50vw"
                className="object-contain p-2 group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-[#181C32] mb-2">
                {t.homepage.credentials.certificateTitle}
              </h3>
              <p className="text-sm text-[#181C32]/70 mb-4">
                {t.homepage.credentials.certificateDescription}
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 group-hover:text-purple-600 transition-colors">
                {t.homepage.credentials.viewImage}
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </div>
          </motion.a>
        </div>
      </section>

      {/* Floating Chat - Sadece ana sayfada görünür - PASIF HALE GETIRILDI */}
      {/* <FloatingChat /> */}
    </div>
  );
}